import React, { useState, useMemo } from 'react'
import { NumberInput, RadioGroup, H3, H2, Select, HintIcon } from '@compeon-os/components'
import styled, { withTheme } from 'styled-components'
import { includes, isEmpty } from 'lodash'

import FormLabel from 'components/FormLabel'
import Suitability from 'components/Suitability'
import StyledSubmitButton from 'components/StyledSubmitButton'
import PotentialProducts from 'components/PotentialProducts'
import { postInquiry, fetchUserId } from 'helpers/api'
import { getSubsidies } from 'helpers/getSubsidies'
import {
  AMOUNT_MATCHES_LIQUIDITY_CONSTRAINT_OPTIONS,
  AMOUNT_MAXIMUM_ONE_QUARTER_OF_2019_TURNOVER_OPTIONS,
  AMOUNT_MAXIMUM_TWICE_OF_2019_LOAN_EXPENSES_OPTIONS,
  AMOUNT_PER_COMPANY_GROUP_OPTIONS,
  PUBLIC_SECTOR_SHARE_OPTIONS,
  ANNUAL_BALANCE_SHEET_TOTAL_OPTIONS,
  ANNUAL_TURNOVER_OPTIONS,
  COULD_HAVE_FINANCED_ITSELF_IN_NON_EMERGENCY_OPTIONS,
  COUNTRY_OPTIONS,
  DESIRED_TERM_OPTIONS,
  MANAGING_DIRECTORS_SALARIES_OPTIONS,
  NUMBER_OF_EMPLOYEES_OPTIONS,
  PREVIOUS_FINANCIAL_PROBLEMS_OPTIONS,
  PRODUCT_OPTIONS,
  PROFIT_OR_DIVIDENDS_DURING_TERM_OPTIONS,
  PURPOSE_OPTIONS,
  FIRST_TURNOVER_OPTIONS,
  PRODUCT_VALUE_LOAN,
  PURPOSE_MAPPING,
  SUM_OF_2017_2019_WAS_A_PROFIT_OPTIONS
} from 'helpers/enums'
import { countsAsSmallCompany } from 'helpers/companySizes'
import { parseQueryParams } from 'helpers/queryParams'
import { trackSubmit, trackFocus } from 'helpers/tracking'
import { parseDecimalNumber } from 'helpers/numbers'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 1rem;
  margin-bottom: 180px;

  @media (min-width: 768px) {
    margin-bottom: 0px;
    flex-direction: row;
  }
`

const StickyContainer = styled.div`
  @media (min-width: 768px) {
    width: 340px;
    position: sticky;
    top: 20px;
  }
`


const Results = styled.div`
  @media (min-width: 768px) {
    padding: 0 30px;
  }
`

const IntroductionText = styled.div`
  border-left: 4px solid ${props => props.theme.primaryColor};
  font-size: 15px;
  padding: 0 15px 0 15px;
  color: ${props => props.theme.secondaryTextColor};
  margin: 20px 0 20px 16px;
`
const StyledH2 = styled(H2)`
  margin: 20px;
  text-align: center;
  line-height: 50px;
`
const FormLabelWithDisable = styled(FormLabel)`
  ${props => props.$disabled && `
    opacity: .5;
  `}
`

const StyledRadioGroup = styled(RadioGroup)`
  position: relative;
  input {
    top: -20px;
  }
  ${props => props.disabled && `
    opacity: .5;
  `}
`

const FormHint = styled.p`
  color: ${props => props.theme.secondaryTextColor};
  font-style: italic;
  margin-top: 0.25rem;
`

const SubmitButtonContainer = styled.div`
  text-align: center;
  width: 100%;
`
const FormLabelWrapper = styled(FormLabel)`
  display: flex;
  align-items: center;

  svg {
    margin-left: 15px;
  }
`
const StyledHintIcon = styled(HintIcon)`
  max-width: 300px;
  font-size: 14px;
  ul {
    padding-left: 20px;
  }
`

const Column = styled.div`
  max-width: 300px;
`

const Form = ({ theme }) => {
  const [additionalInformation, setAdditionalInformation] = useState({})

  const { matches, potentialMatches } = useMemo(() => {
    return getSubsidies(additionalInformation)
  }, [additionalInformation])

  const isAnyProductSuitable = Boolean(matches.length)
  const isAnyProductPotentiallySuitable = Boolean(potentialMatches.length)

  const isSmallCompany = countsAsSmallCompany({
    numberOfEmployees: additionalInformation['number-of-employees']?.value,
    annualTurnover: additionalInformation['annual-turnover']?.value,
    annualBalanceSheetTotal: additionalInformation['annual-balance-sheet-total']?.value
  })

  const addAdditionalInfo = ({ key, value, label }) => {
    setAdditionalInformation({
      ...additionalInformation,
      [key]: { key, value, label, kind: 'string' }
    })
  }

  const getRadioInfoOnChange = (key, label) => value => {
    addAdditionalInfo({ key, value, label })
  }

  const getNumberInputChange = (key, label) => ({ target: { value } }) => {
    const parsedValue = parseDecimalNumber(value)
    addAdditionalInfo({ key, value: parsedValue, label })
  }

  const getSelectInputChange = (key, label) => ({ target: { value } }) => {
    addAdditionalInfo({ key, value, label })
  }

  const onSubmit = async (e) => {
    e.preventDefault()

    const queryParams = parseQueryParams()
    const { product: productInfo, purpose: purposeInfo } = additionalInformation

    const isLoan = productInfo?.value === PRODUCT_VALUE_LOAN
    const derivedPurposeKind = PURPOSE_MAPPING[purposeInfo?.value]

    const inquiryData = {
      'additional-information': Object.values(additionalInformation),
    }

    if (isLoan) {
      inquiryData['product-kind'] = includes(['machine', 'vehicle'], derivedPurposeKind) ? 'investment_loan' : 'loan'
    }

    if (derivedPurposeKind) inquiryData['purpose-kind'] = derivedPurposeKind
    if (isAnyProductSuitable) inquiryData['workflow-config'] = { corona: matches }
    if (isLoan && isAnyProductSuitable) inquiryData['consider-subsidies'] = true

    trackSubmit()

    const referralCode = queryParams?.get('referral-code') || queryParams?.get('referralCode')
    const multiplierId = queryParams?.get('multiplierId')
    const companyId = queryParams?.get('companyId')
    const userId = await fetchUserId()

    postInquiry(inquiryData, userId, companyId, multiplierId, referralCode)
      .then(res => {
        const inquiryId = res.data.data.id

        window.location.href =
          `${process.env.REACT_APP_WIZARD_URL}/inquiries/${inquiryId}`
    })
  }

  const touched = !isEmpty(additionalInformation)

  return (
    <>
      <StyledH2>Corona-Hilfskredit der KfW</StyledH2>
      <IntroductionText>
          Bitte beantworten Sie folgende Fragen, um direkt zu erfahren,
          ob Sie die Corona-Hilfe der KfW für Ihr Unternehmen nutzen können.
          Falls nicht, können Sie Ihren Finanzierungswunsch trotzdem anfragen.
      </IntroductionText>

      <Wrapper>
        <form>
          <H3>Fördermittelprüfung</H3>

          <FormLabel>Wie hoch ist Ihr zu finanzierender Betrag?</FormLabel>
          <Column>
            <NumberInput
              name='amount'
              value={additionalInformation['amount']?.value || ''}
              onFocus={() => trackFocus('amount')}
              addon='€'
              placeholder='in Euro'
              onChange={getNumberInputChange(
                'amount',
                'Finanzierungsbetrag'
              )}
            />
          </Column>

          <FormLabel>Liegt Ihr Unternehmenssitz in Deutschland?</FormLabel>
          <StyledRadioGroup
            name='country'
            value={additionalInformation['country']?.value}
            onFocus={() => trackFocus('country')}
            options={COUNTRY_OPTIONS}
            onChange={getRadioInfoOnChange(
              'country',
              'Unternehmenssitz'
            )}
          />

          <FormLabel>Vor wie vielen Jahren haben Sie Ihre Geschäftstätigkeit aufgenommen?</FormLabel>
          <FormHint>Als Aufnahme der Geschäftstätigkeit zählt der Zeitpunkt der ersten Umsatzerzielung.</FormHint>
          <Select
            name='first-turnover'
            placeholder='Bitte wählen'
            onFocus={() => trackFocus('first-turnover')}
            value={additionalInformation['first-turnover']?.value || ''}
            onChange={getSelectInputChange(
              'first-turnover',
              'Zeitpunkt der Aufnahme der Geschäftstätigkeit'
            )}
            options={FIRST_TURNOVER_OPTIONS}
          />

          <FormLabelWrapper>
            Wie viele Mitarbeiter beschäftigen Sie?
            <StyledHintIcon color={theme.primaryColor}>
              Um den KfW Schnellkredit mit 100% Haftungsfreistellung (078) zu erhalten, müssen Sie mind. 10,1 vollzeitäquivalente Mitarbeiter per 31.12.2019 beschäftigt haben.
              Bitte rechnen Sie Teilzeitkräfte und Minijobber wie folgt um:
              <ul>
                <li>bis 20 Wochenstunden = Faktor 0,5</li>
                <li>bis 30 Wochenstunden = Faktor 0,75</li>
                <li>mehr als 30 Stunden & Auszubildende = Faktor 1</li>
                <li>Mitarbeiter auf 450 Euro-Basis = Faktor 0,3</li>
              </ul>
              Der Unternehmer ist selbst mitzuzählen.
              Als Beschäftigter zählt, wer am Stichtag 31.12.2019 einen laufenden Arbeitsvertrag hat/hatte. Leih- und Fremdarbeiter werden nicht mitgezählt. Mitarbeiter in Elternzeit dürfen mitgezählt werden.
            </StyledHintIcon>
          </FormLabelWrapper>
          <Select
            name='number-of-employees'
            placeholder='Bitte wählen'
            value={additionalInformation['number-of-employees']?.value || ''}
            onFocus={() => trackFocus('number-of-employees')}
            onChange={getSelectInputChange(
              'number-of-employees',
              'Anzahl der Mitarbeiter'
            )}
            options={NUMBER_OF_EMPLOYEES_OPTIONS}
          />

          <FormLabel>Wie hoch ist Ihr Jahresumsatz?</FormLabel>
          <StyledRadioGroup
            name='annual-turnover'
            value={additionalInformation['annual-turnover']?.value}
            onFocus={() => trackFocus('annual-turnover')}
            options={ANNUAL_TURNOVER_OPTIONS}
            onChange={getRadioInfoOnChange(
              'annual-turnover',
              'Jahresumsatz'
            )}
          />

          <FormLabel>Wie hoch ist Ihre Jahresbilanzsumme?</FormLabel>
          <StyledRadioGroup
            name='annual-balance-sheet-total'
            value={additionalInformation['annual-balance-sheet-total']?.value}
            onFocus={() => trackFocus('annual-balance-sheet-total')}
            options={ANNUAL_BALANCE_SHEET_TOTAL_OPTIONS}
            onChange={getRadioInfoOnChange(
              'annual-balance-sheet-total',
              'Jahresbilanzsumme'
            )}
          />

          <FormLabel>Die jährlichen Geschäftsführergehälter (inkl. geldwerter Vorteile und Tantiemen) liegen pro Geschäftsführer</FormLabel>
          <Select
            name='managing-directors-salaries'
            placeholder='Bitte wählen'
            value={additionalInformation['managing-directors-salaries']?.value}
            onFocus={() => trackFocus('managing-directors-salaries')}
            options={MANAGING_DIRECTORS_SALARIES_OPTIONS}
            onChange={getSelectInputChange(
              'managing-directors-salaries',
              'Geschäftsführergehälter'
            )}
          />

           <FormLabelWrapper>
            Wurde im Zeitraum der Jahre 2017-2019 in Summe ein Gewinn erzielt?
            <StyledHintIcon color={theme.primaryColor}>
              Für das Programm „KfW Schnellkredit mit 100% Haftungsfreistellung“ zählt als Gewinn der kumulierte Jahresgewinn vor Steuern der Jahre 2017-2019:
              <ul>
                <li>Alle Werte müssen einen positiven Saldo ergeben, so dass ein oder zwei Einzeljahre mit Verlust zu keinem Ausschluss führen.</li>
                <li>Bei bilanzierenden Unternehmen muss die Gewinnermittlung nach HGB erfolgen (keine Steuerbilanz).</li>
              </ul>
              Wahlrecht bei Kapitalgesellschaften (z.B. GmbH):
              <ul>
                <li>Die Geschäftsführergehälter p.a. Jahre dürfen auf den Gewinn vor Steuern addiert werden.</li>
              </ul>
            </StyledHintIcon>
          </FormLabelWrapper>
          <StyledRadioGroup
            name='sum-of-2017-2019-was-a-profit'
            value={additionalInformation['sum-of-2017-2019-was-a-profit']?.value}
            onFocus={() => trackFocus('sum-of-2017-2019-was-a-profit')}
            options={SUM_OF_2017_2019_WAS_A_PROFIT_OPTIONS}
            onChange={getRadioInfoOnChange(
              'sum-of-2017-2019-was-a-profit',
              'Wurde im Zeitraum der Jahre 2017-2019 in Summe ein Gewinn erzielt?'
            )}
          />

          <FormLabel>Wieviele Anteile am Unternehmen hält die öffentlich Hand?</FormLabel>
          <Select
            name='public-sector-share'
            placeholder='Bitte wählen'
            value={additionalInformation['public-sector-share']?.value}
            onFocus={() => trackFocus('public-sector-share')}
            options={PUBLIC_SECTOR_SHARE_OPTIONS}
            onChange={getSelectInputChange(
              'public-sector-share',
              'Hat Ihr Unternehmen die Beteiligung der öffentlichen Hand?'
            )}
          />

          <FormLabel>Befand sich das Unternehmen vor dem 31.12.2019 in finanziellen Schwierigkeiten?</FormLabel>
          <FormHint>Das Unternehmen wies am 31.12.2019 geordnete wirtschaftliche Verhältnisse aus, d.h. es gab keine ungeregelten Zahlungsrückstände von mehr als 30 Tagen, keine Stundungsvereinbarungen oder Tilgungsaussetzungen und keine Covenant-Brüche gegenüber Banken.</FormHint>
          <StyledRadioGroup
            name='previous-financial-problems'
            value={additionalInformation['previous-financial-problems']?.value}
            onFocus={() => trackFocus('previous-financial-problems')}
            options={PREVIOUS_FINANCIAL_PROBLEMS_OPTIONS}
            onChange={getRadioInfoOnChange(
              'previous-financial-problems',
              'Das Unternehmen befand sich bis zum 31.12.2019 nicht in finanziellen Schwierigkeiten'
            )}
          />

          <FormLabel>Trifft diese Aussage auf Ihr Unternehmen zu? Auf Basis einer normalen wirtschaftlichen Situation wie vor der Krise wäre eine Durchfinanzierung des Unternehmens bis 31.12.2020 voraussichtlich gegeben gewesen.</FormLabel>
          <StyledRadioGroup
            name='could-have-financed-itself-in-non-emergency'
            value={additionalInformation['could-have-financed-itself-in-non-emergency']?.value}
            onFocus={() => trackFocus('could-have-financed-itself-in-non-emergency')}
            options={COULD_HAVE_FINANCED_ITSELF_IN_NON_EMERGENCY_OPTIONS}
            onChange={getRadioInfoOnChange(
              'could-have-financed-itself-in-non-emergency',
              'Auf Basis einer normalen wirtschaftlichen Situation wie vor der Krise wäre eine Durchfinanzierung des Unternehmens bis 31.12.2020 voraussichtlich gegeben'
            )}
          />

          <FormLabel>Was möchten Sie finanzieren?</FormLabel>
          <Select
            name='purpose'
            onFocus={() => trackFocus('purpose')}
            placeholder='Bitte wählen'
            value={additionalInformation['purpose']?.value || ''}
            onChange={getSelectInputChange(
              'purpose',
              'Was möchten Sie finanzieren?'
            )}
            options={PURPOSE_OPTIONS}
          />

          <FormLabel>Welches Finanzprodukt wünschen Sie?</FormLabel>
          <StyledRadioGroup
            name='product'
            value={additionalInformation['product']?.value}
            onFocus={() => trackFocus('product')}
            options={PRODUCT_OPTIONS}
            onChange={getRadioInfoOnChange(
              'product',
              'Welches Finanzprodukt wünschen Sie?'
            )}
          />

          <FormLabel>In welchem Rahmen bewegt sich Ihr Kreditwunsch (pro Unternehmensgruppe)?</FormLabel>
          <Select
            name='amount-per-company-group'
            placeholder='Bitte wählen'
            value={additionalInformation['amount-per-company-group']?.value || ''}
            onFocus={() => trackFocus('amount-per-company-group')}
            onChange={getSelectInputChange(
              'amount-per-company-group',
              'Kreditbetrag pro Unternehmensgruppe'
            )}
            options={AMOUNT_PER_COMPANY_GROUP_OPTIONS}
          />

          <FormLabelWithDisable
            $disabled={!additionalInformation.amount?.value}>
              Der Jahresumsatz 2019 betrug mehr als {(4 * additionalInformation.amount?.value) || '<Wunschbetrag x 4>'} €
          </FormLabelWithDisable>
          <StyledRadioGroup
            name='amount-maximum-one-quarter-of-2019-turnover'
            disabled={!additionalInformation.amount?.value}
            value={additionalInformation['amount-maximum-one-quarter-of-2019-turnover']?.value}
            onFocus={() => trackFocus('amount-maximum-one-quarter-of-2019-turnover')}
            options={AMOUNT_MAXIMUM_ONE_QUARTER_OF_2019_TURNOVER_OPTIONS}
            onChange={getRadioInfoOnChange(
              'amount-maximum-one-quarter-of-2019-turnover',
              'Der Kreditbetrag beträgt maximal 25% des Jahresumsatzes 2019'
            )}
          />

          <FormLabelWithDisable
            $disabled={!additionalInformation.amount?.value}>
              Die Lohnkosten 2019 betrugen mehr als {(Math.trunc(0.5 * additionalInformation.amount?.value)) || '<Wunschbetrag x 0.5>'} €
          </FormLabelWithDisable>
          <StyledRadioGroup
            name='amount-maximum-twice-of-2019-loan-expenses'
            disabled={!additionalInformation.amount?.value}
            value={additionalInformation['amount-maximum-twice-of-2019-loan-expenses']?.value}
            onFocus={() => trackFocus('amount-maximum-twice-of-2019-loan-expenses')}
            onChange={getRadioInfoOnChange(
              'amount-maximum-twice-of-2019-loan-expenses',
              'Der Kreditbetrag beträgt maximal das Doppelte der Lohnkosten 2019'
            )}
            options={AMOUNT_MAXIMUM_TWICE_OF_2019_LOAN_EXPENSES_OPTIONS}
          />

          <FormLabelWithDisable $disabled={!additionalInformation.amount?.value}>
            Der aktuelle Liquiditätsbedarf für die nächsten {' '}
            {isSmallCompany ? 18 : 12} {' '}
            Monate liegt über {' '}
            {additionalInformation.amount?.value || '<Wunschbetrag>'} €
          </FormLabelWithDisable>
          <StyledRadioGroup
            disabled={!additionalInformation.amount?.value}
            value={additionalInformation['amount-matches-liquidity-constraint']?.value}
            onFocus={() => trackFocus('amount-matches-liquidity-constraint')}
            onChange={getRadioInfoOnChange(
              'amount-matches-liquidity-constraint',
              `Der Kreditbetrag beträgt maximal den aktuellen Liquiditätsbedarf für die nächsten ${isSmallCompany ? 18 : 12} Monate`
            )}
            name='amount-matches-liquidity-constraint'
            options={AMOUNT_MATCHES_LIQUIDITY_CONSTRAINT_OPTIONS}
          />

          <FormLabel>Welche Laufzeit soll der Kredit haben?</FormLabel>
          <Select
            name='desired-term'
            placeholder='Bitte wählen'
            value={additionalInformation['desired-term']?.value || ''}
            onFocus={() => trackFocus('desired-term')}
            onChange={getSelectInputChange(
              'desired-term',
              'Welche Laufzeit soll der Kredit haben?'
            )}
            options={DESIRED_TERM_OPTIONS}
          />


          <FormLabel>Soll für das abgelaufene Geschäftsjahr 2019 und / oder sollen während der Kreditlaufzeit Gewinne oder Dividenden an die Gesellschafter ausgeschüttet werden?</FormLabel>
          <StyledRadioGroup
            name='profit-or-dividends-during-term'
            value={additionalInformation['profit-or-dividends-during-term']?.value}
            onFocus={() => trackFocus('profit-or-dividends-during-term')}
            options={PROFIT_OR_DIVIDENDS_DURING_TERM_OPTIONS}
            onChange={getRadioInfoOnChange(
              'profit-or-dividends-during-term',
              'Für das abgelaufene Geschäftsjahr 2019 und / oder während der Laufzeit der Finanzierung sollen Gewinne oder Dividenden ausgeschüttet werden'
            )}
          />
        </form>

        <Results>
          <StickyContainer>
            {touched &&  <PotentialProducts products={potentialMatches} />}
            <Suitability
              isPotentiallySuitable={isAnyProductPotentiallySuitable}
              isSuitable={isAnyProductSuitable}
              touched={touched}
              onSubmit={onSubmit}
            />
            {isAnyProductSuitable &&
              <SubmitButtonContainer>
                <StyledSubmitButton small type='submit' onClick={onSubmit}>
                  Fördermittel jetzt anfragen
                </StyledSubmitButton>
              </SubmitButtonContainer>
            }
          </StickyContainer>
        </Results>
      </Wrapper>
    </>
  )
}

export default withTheme(Form)
